import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { loadPublicResourcesForHub } from "@/lib/query-registry";
import { ResourceHub, collectionPageJsonLd } from "@/components/site/ResourceHub";
import { SITE_URL } from "@/lib/i18n";
import coverPerf from "@/assets/cover-pillar-performance.jpg";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const PATH = "/resources/performance";
const ALT = "/es/recursos/rendimiento";
const FIELD_NOTE = "/resources/sitewide-lcp-regression-36-urls";
const TITLE = "Shopify Performance Resources & Playbooks · Shugert";
const DESC =
  "Browse field-tested Shopify speed playbooks and transparent performance field notes covering LCP, INP, CLS, apps, images and Core Web Vitals.";

export const Route = createFileRoute("/resources/performance")({
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    return loadPublicResourcesForHub(context.queryClient);
  },
  head: ({ loaderData }) => {
    const resources = (loaderData as any)?.resources ?? [];
    const urls = resources
      .filter((r: any) =>
        (r.tags_en ?? []).join(" ").toLowerCase().match(/performance|speed|core web vitals|lcp|inp|cls/),
      )
      .map((r: any) => `${SITE_URL}/resources/${r.slug}`);
    urls.push(`${SITE_URL}${FIELD_NOTE}`);
    return buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: coverPerf,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Performance", path: PATH },
        ]),
        collectionPageJsonLd("Shopify Performance Resources", `${SITE_URL}${PATH}`, DESC, urls),
      ],
    });
  },
  component: () => (
    <SiteShell>
      <ResourceHub
        locale="en"
        cluster="performance"
        copy={{
          eyebrow: "Performance resources",
          title: "Shopify performance resources: playbooks, checklists and field notes.",
          intro:
            "Start with the full performance guide, then use the focused resources to investigate apps, images, Core Web Vitals and release regressions. Claims are separated from field validation so the material remains useful after the headline changes.",
          serviceLabel: "See Shopify Performance",
          serviceHref: "/shopify-performance",
          articlesTitle: "Performance guides",
          articlesEmpty: "More performance guides coming soon. In the meantime, request an audit.",
          read: "Read",
          min: "min",
          by: "By",
          updated: "Updated",
          backLabel: "Resources",
          backHref: "/resources",
        }}
      />
      <section className="mx-auto -mt-24 max-w-7xl px-6 pb-28">
        <a
          href={FIELD_NOTE}
          className="block rounded-3xl border border-border bg-card p-8 transition-colors hover:border-foreground/30 sm:p-10"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">New field note · July 2026</p>
          <h2 className="mt-3 max-w-3xl font-display text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
            How we diagnosed a site-wide LCP regression across 36 URLs
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            The affected URL group, the shared causes we tested, the bounded changes we shipped and why deployment is not the same as a resolved Search Console field issue.
          </p>
          <span className="mt-6 inline-flex text-sm font-semibold text-foreground">Read the field note →</span>
        </a>
      </section>
    </SiteShell>
  ),
});
